/**
 * The access token lives ONLY here: a plain module-level variable, never
 * written to localStorage/sessionStorage/IndexedDB/a cookie. It's lost on
 * every full page reload by design -- AuthProvider re-establishes it via a
 * silent refresh (the HttpOnly refresh-token cookie) on mount.
 *
 * This module has no React dependency on purpose: apiFetch.ts (a plain
 * fetch wrapper, not a hook) needs to read/write the current token too, and
 * a plain module-level store is the simplest way to share that single
 * source of truth between React state and imperative fetch code without
 * ever touching a Storage API.
 */

type Listener = (token: string | null) => void;

let accessToken: string | null = null;
const listeners = new Set<Listener>();

export const getAccessToken = (): string | null => accessToken;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
  listeners.forEach((fn) => fn(token));
};

/** Lets AuthProvider's React state stay in sync with writes made from apiFetch (e.g. after a silent refresh). */
export const subscribeAccessToken = (fn: Listener): (() => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
